<template>
  <div class="entry-detail-container page-container bg-light-gradient">
    <div class="container-padded py-4">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="mb-0 animate-fade-in display-heading">
          <span class="icon icon-primary icon-circle me-2"><i class="fas fa-clipboard-list"></i></span> Test-Ergebnis
        </h1>
        <router-link to="/entries" class="btn btn-outline-secondary btn-with-icon animate-fade-in with-hover-effect">
          <span class="icon me-2"><i class="fas fa-arrow-left"></i></span> Zurück zur Liste
        </router-link>
      </div>
      
      <div v-if="loading" class="text-center py-5 content-loading">
        <div class="loader loader-lg mb-3">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Wird geladen...</span>
          </div>
        </div>
        <p class="text-muted">Daten werden geladen...</p>
      </div>
      
      <template v-else>
        <div class="row">
          <div class="col-lg-4 mb-4">
            <Card animateIn="fade-in-up" class="card-profile-summary">
              <template v-slot:header>
                <h4 class="card-title mb-0">Teilnehmerinformationen</h4>
              </template>
              
              <div class="participant-info content-padded">
                <div class="info-group mb-3">
                  <label class="form-label label-emphasized text-muted">Name</label>
                  <p class="mb-0 fs-5 fw-medium">{{ entry.first_name }} {{ entry.last_name }}</p>
                </div>
                
                <div class="info-group mb-3">
                  <label class="form-label label-emphasized text-muted">E-Mail</label>
                  <p class="mb-0">
                    <a :href="`mailto:${entry.email}`" class="link-primary with-underline-effect">{{ entry.email }}</a>
                  </p>
                </div>
                
                <div class="info-group mb-3">
                  <label class="form-label label-emphasized text-muted">Datum</label>
                  <p class="mb-0 with-icon">
                    <span class="icon icon-muted me-2"><i class="fas fa-calendar-alt"></i></span>
                    {{ formatDate(entry.created_at) }}
                  </p>
                </div>
                
                <div class="info-group">
                  <label class="form-label label-emphasized text-muted">Bearbeitungsdauer</label>
                  <p class="mb-0 with-icon">
                    <span class="icon icon-muted me-2"><i class="fas fa-clock"></i></span>
                    {{ formatDuration(entry.duration) }}
                  </p>
                </div>
              </div>
              
              <template v-slot:footer>
                <button class="btn btn-primary btn-with-icon with-hover-effect" @click="showEmailModal">
                  <span class="icon me-2"><i class="fas fa-envelope"></i></span> E-Mail senden
                </button>
              </template>
            </Card>
          </div>
          
          <div class="col-lg-8 mb-4">
            <Card animateIn="fade-in-up" delayIn="100" class="card-with-results">
              <template v-slot:header>
                <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <h4 class="card-title mb-0">Testergebnis</h4>
                  <span class="badge score-badge" :class="getScoreBadgeClass(calculatePercentage(entry.score, entry.max_score))">
                    {{ entry.score }}/{{ entry.max_score }} Punkte ({{ calculatePercentage(entry.score, entry.max_score) }}%)
                  </span>
                </div>
              </template>
              
              <div class="content-padded">
                <div class="mb-4">
                  <h5 class="test-name fw-medium">{{ entry.test.name }}</h5>
                  <div class="progress progress-enhanced mb-3">
                    <div 
                      class="progress-bar" 
                      :class="getScoreProgressClass(calculatePercentage(entry.score, entry.max_score))"
                      :style="{ width: calculatePercentage(entry.score, entry.max_score) + '%' }"
                      role="progressbar" 
                      :aria-valuenow="calculatePercentage(entry.score, entry.max_score)"
                      aria-valuemin="0" 
                      aria-valuemax="100"
                    ></div>
                  </div>
                </div>
                
                <div class="answers-list">
                  <h5 class="section-title mb-3 fw-medium">Antworten</h5>
                  
                  <div 
                    v-for="(answer, index) in entry.answers" 
                    :key="answer.id"
                    class="answer-item mb-3 p-3 rounded border"
                    :class="getAnswerItemClass(answer)"
                  >
                    <div class="d-flex align-items-start">
                      <span class="answer-number fw-bold me-3">Frage {{ index + 1 }}</span>
                      <div class="flex-grow-1">
                        <div v-html="answer.question.description" class="question-description mb-3 fw-medium"></div>
                        
                        <div v-if="answer.question.type === 'multiple_choice'" class="question-options">
                          <div 
                            v-for="(optionText, optIndex) in getQuestionOptions(answer.question)" 
                            :key="`answer_${answer.id}_${optIndex}`"
                            class="option-item d-flex align-items-center mb-2 p-2 rounded small"
                            :class="getOptionClass(answer, answer.question, optIndex + 1)"
                          >
                            <span class="option-icon me-2 fs-5" >
                              <i class="fas" :class="getOptionIconClass(answer, answer.question, optIndex + 1)"></i>
                            </span>
                            <span class="option-text flex-grow-1">{{ optionText }}</span>
                            <span 
                              v-if="isCorrectOption(answer.question, optIndex + 1) && !isSelectedOption(answer, optIndex + 1)"
                              class="badge bg-success-light text-success ms-2"
                            >
                              Korrekt
                            </span>
                            <span
                              v-if="isSelectedOption(answer, optIndex + 1) && !isCorrectOption(answer.question, optIndex + 1)"
                              class="badge bg-danger-light text-danger ms-2"
                            >
                              Falsch
                            </span>
                          </div>
                        </div>
                        
                        <div v-else class="text-answer mt-2">
                          <p class="fw-medium mb-1 small text-muted">Gegebene Antwort:</p>
                          <div class="p-2 rounded bg-light">
                            <p class="mb-0 text-answer-content" :class="{ 'text-muted fst-italic': !answer.open_answer }">
                              {{ answer.open_answer || '(Keine Antwort)' }}
                            </p>
                          </div>
                        </div>
                        
                        <!-- Punkteanzeige nur für Multiple-Choice-Fragen -->
                        <div v-if="answer.question.type === 'multiple_choice'" class="mt-3 text-end text-muted small">
                          Erreichte Punkte: {{ answer.points_earned !== null ? answer.points_earned : '-' }} / {{ answer.question.points || '-' }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- <template v-slot:footer>
                <div class="d-flex justify-content-between flex-wrap gap-2">
                  <button class="btn btn-primary btn-with-icon with-hover-effect" @click="printResult">
                    <span class="icon me-2"><i class="fas fa-print"></i></span> Drucken
                  </button>
                  <button class="btn btn-outline-primary btn-with-icon with-hover-effect" @click="exportPDF">
                    <span class="icon me-2"><i class="fas fa-file-pdf"></i></span> Als PDF exportieren
                  </button>
                </div>
              </template> -->
            </Card>
          </div>
        </div>
      </template>
      
      <b-modal 
        id="email-modal" 
        title="E-Mail an Teilnehmer senden"
        ok-title="Senden"
        cancel-title="Abbrechen"
        @ok="sendEmailToParticipant"
        centered
      >
        <div class="modal-form">
          <div class="mb-3">
            <label for="emailSubject" class="form-label label-emphasized">Betreff</label>
            <input 
              type="text" 
              class="form-control with-focus-effect" 
              id="emailSubject" 
              v-model="emailData.subject" 
              placeholder="Betreff eingeben..."
              required
            >
          </div>
          <div class="mb-3">
            <label for="emailMessage" class="form-label label-emphasized">Nachricht</label>
            <textarea 
              class="form-control with-focus-effect" 
              id="emailMessage" 
              v-model="emailData.message" 
              rows="5"
              placeholder="Nachricht eingeben..."
              required
            ></textarea>
          </div>
          <div class="form-check mb-3">
            <input 
              class="form-check-input" 
              type="checkbox" 
              id="includeResults" 
              v-model="emailData.includeResults"
            >
            <label class="form-check-label" for="includeResults">
              Testergebnis anhängen
            </label>
          </div>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useToast } from 'vue-toastification'; // Assuming vue-toastification is used
import { useModal } from 'bootstrap-vue-next'; // Check if this is the correct import for your modal
import Card from '@/components/ui/Card.vue';
import { useEntriesStore } from '@/stores/entries';
import { useAuthStore } from '@/stores/auth';

export default {
  name: 'EntryDetail',
  components: {
    Card
  },
  setup() {
    const route = useRoute();
    const entriesStore = useEntriesStore();
    const authStore = useAuthStore();
    const toast = useToast(); // Use toast instance
    // Modal instance might need specific handling based on the library (e.g., bootstrap-vue-next)
    // For now, let's assume a way to control it, replace with actual implementation if needed
    // const modal = useModalController(); // Placeholder

    // --- Reactive State (from data) ---
    const loading = ref(true);
    const entry = ref({});
    const emailData = ref({
      subject: '',
      message: '',
      includeResults: true
    });

    // --- Computed Properties ---
    const currentUser = computed(() => authStore.user);
    const entryId = computed(() => route.params.id);
    // Get current entry reactively from the store
    const currentEntryFromStore = computed(() => entriesStore.getCurrentEntry);

    // --- Methods --- 
    const loadEntryData = async () => {
      loading.value = true;
      try {
        await entriesStore.fetchEntry(entryId.value);
        // Use the computed property from the store
        entry.value = { ...currentEntryFromStore.value }; 
        if (!entry.value || Object.keys(entry.value).length === 0) {
           console.error('Entry data not found in store after fetch for ID:', entryId.value);
           toast.error('Eintrag nicht gefunden oder Ladefehler.');
           // Potentially redirect or show a not found message
        } else {
           console.log('Entry data loaded successfully:', entry.value);
        }
      } catch (error) {
        toast.error('Fehler beim Laden der Daten');
        console.error('Error loading entry data:', error);
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '';
      const options = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' };
      return new Date(dateString).toLocaleDateString('de-DE', options);
    };

    const formatDuration = (seconds) => {
      if (!seconds && seconds !== 0) return '—';
      const totalSeconds = Number(seconds);
       if (isNaN(totalSeconds)) return '—';

      const minutes = Math.floor(totalSeconds / 60);
      const remainingSeconds = totalSeconds % 60;
      return `${minutes} Minuten ${remainingSeconds} Sekunden`;
    };

    const calculatePercentage = (score, maxScore) => {
      const numScore = Number(score);
      const numMaxScore = Number(maxScore);
      if (isNaN(numScore) || isNaN(numMaxScore) || numMaxScore === 0) return 0;
      return Math.round((numScore / numMaxScore) * 100);
    };

    const getScoreBadgeClass = (percentage) => {
      if (percentage >= 80) return 'bg-success';
      if (percentage >= 60) return 'bg-primary';
      if (percentage >= 40) return 'bg-warning';
      return 'bg-danger';
    };

    const getScoreProgressClass = (percentage) => {
      if (percentage >= 80) return 'bg-success';
      if (percentage >= 60) return 'bg-primary';
      if (percentage >= 40) return 'bg-warning';
      return 'bg-danger';
    };

    const getQuestionOptions = (question) => {
      const options = [];
      if (!question) return options;
      for (let i = 1; i <= 6; i++) {
        if (question[`answer_${i}`]) {
          options.push(question[`answer_${i}`]);
        } else {
          if (question.hasOwnProperty(`answer_${i}`) && question[`answer_${i}`] == null) break;
        }
      }
      return options;
    };

    const isSelectedOption = (answer, optionNumber) => {
      if (!answer) return false;
      if (typeof answer[`answer_${optionNumber}_selected`] === 'boolean') {
          return answer[`answer_${optionNumber}_selected`];
      }
      const selectedOptions = Array.isArray(answer.selected_options)
          ? answer.selected_options
          : (typeof answer.selected_options === 'string' && answer.selected_options !== ''
              ? answer.selected_options.split(',').map(Number)
              : []);
      return selectedOptions.includes(optionNumber);
    };

    const isCorrectOption = (question, optionNumber) => {
      if (!question) return false;
      if (typeof question[`is_answer_${optionNumber}_correct`] === 'boolean') {
        return question[`is_answer_${optionNumber}_correct`];
      }
      if (Array.isArray(question.correct_options)) {
        return question.correct_options.includes(optionNumber);
      }
      if (typeof question.correct_option === 'number') {
        return question.correct_option === optionNumber;
      }
      return false;
    };

    const getAnswerItemClass = (answer) => {
      if (!answer || !answer.question) return 'border-light';
      
      // Für offene Fragen: Keine Bewertung, nur neutraler Rahmen
      if (answer.question.type === 'open') {
        return 'border-light';
      }
      
      // Für Multiple-Choice-Fragen: Punkte-basierte Darstellung
      const pointsEarned = Number(answer.points_earned);
      const maxPoints = Number(answer.question.points);
      if (isNaN(pointsEarned) || isNaN(maxPoints) || maxPoints === 0) {
          return 'border-light';
      }
      if (pointsEarned >= maxPoints) {
          return 'answer-correct border-success-light';
      }
      if (pointsEarned < maxPoints) {
          return 'answer-incorrect border-danger-light';
      }
      return 'border-light';
    };

    const getOptionClass = (answer, question, optionNumber) => {
      const isSelected = isSelectedOption(answer, optionNumber);
      const isCorrect = isCorrectOption(question, optionNumber);
      if (isSelected && isCorrect) return 'option-correct bg-success-light';
      if (isSelected && !isCorrect) return 'option-incorrect bg-danger-light';
      if (!isSelected && isCorrect) return 'option-missed-correct bg-light';
      return 'option-neutral bg-light';
    };

    const getOptionIconClass = (answer, question, optionNumber) => {
      const isSelected = isSelectedOption(answer, optionNumber);
      const isCorrect = isCorrectOption(question, optionNumber);
      if (isSelected && isCorrect) return 'fa-check-circle text-success';
      if (isSelected && !isCorrect) return 'fa-times-circle text-danger';
      if (!isSelected && isCorrect) return 'fa-check text-success';
      return 'fa-circle text-muted';
    };

    const printResult = () => {
      window.print();
    };

    const exportPDF = () => {
      toast.info('PDF wird generiert...');
      setTimeout(() => {
        toast.success('PDF wurde erfolgreich generiert und heruntergeladen');
      }, 1500);
    };

    const showEmailModal = () => {
       if (!entry.value || !entry.value.test) {
           toast.error('Eintragsdaten unvollständig, E-Mail kann nicht vorbereitet werden.');
           return;
       }
      emailData.value.subject = `Ihr Testergebnis: ${entry.value.test.name}`;
      emailData.value.message = `Hallo ${entry.value.first_name},

vielen Dank für die Teilnahme an unserem Test "${entry.value.test.name}".

Sie haben ${entry.value.score} von ${entry.value.max_score} Punkten erreicht (${calculatePercentage(entry.value.score, entry.value.max_score)}%).

Mit freundlichen Grüßen
Das Team`;
      // Here you would trigger the modal display using your modal library's method
      // e.g., modal.show('email-modal'); or find the component ref and call a method
      // For now, just logging:
      console.log('Triggering email modal display (replace with actual modal logic)');
      // If using bootstrap-vue-next directly:
      // const modalInstance = document.getElementById('email-modal');
      // if (modalInstance) { new Modal(modalInstance).show(); }
    };

    const sendEmailToParticipant = async (/* bvModalEvent */) => {
      // bvModalEvent.preventDefault(); // Might be needed depending on modal library

      if (!emailData.value.subject.trim() || !emailData.value.message.trim()) {
        toast.error('Bitte füllen Sie alle Felder aus');
        return;
      }

      try {
        toast.info('E-Mail wird gesendet...');
        await entriesStore.sendEmail({
          entry_id: entryId.value,
          ...emailData.value
        });
        toast.success('E-Mail wurde erfolgreich gesendet');
         // Here you would trigger the modal hide
         // e.g., modal.hide('email-modal');
         console.log('Triggering email modal hide (replace with actual modal logic)');
      } catch (error) {
        toast.error('Fehler beim Senden der E-Mail');
        console.error('Error sending email:', error);
      }
    };

    // --- Lifecycle Hook (created equivalent) ---
    onMounted(() => {
      loadEntryData();
    });

    // --- Return everything needed by the template ---
    return {
      loading,
      entry,
      emailData,
      currentUser,
      entryId,
      // Methods to be used in template
      loadEntryData, 
      formatDate,
      formatDuration,
      calculatePercentage,
      getScoreBadgeClass,
      getScoreProgressClass,
      getQuestionOptions,
      isSelectedOption,
      isCorrectOption,
      getAnswerItemClass,
      getOptionClass,
      getOptionIconClass,
      printResult,
      exportPDF,
      showEmailModal, // Renamed from sendEmail to avoid confusion
      sendEmailToParticipant
    };
  },
  // Remove the old options API blocks if they exist
  // data() { return {}; },
  // computed: {},
  // created() {},
  // methods: {}
};
</script>

<style lang="scss" scoped>
.answer-item {
  transition: background-color 0.3s ease;
  // border-width: 1px; // Ensure border is visible
  // border-style: solid; // Ensure border is visible
}

.answer-correct {
  // background-color: rgba(var(--bs-success-rgb), 0.1); // Light green background
  // border-color: rgba(var(--bs-success-rgb), 0.3);
}

.answer-incorrect {
  // background-color: rgba(var(--bs-danger-rgb), 0.1); // Light red background
  // border-color: rgba(var(--bs-danger-rgb), 0.3);
}

.answer-number {
    min-width: 70px; /* Ensure enough space for "Frage XX" */
    text-align: center;
}


.question-description {
  // Style for the question text itself
}

.option-item {
  // Base styling for each option row
   border: 1px solid transparent; // Base border
   transition: border-color 0.2s ease-in-out;

   &.option-correct {
      // background-color: rgba(var(--bs-success-rgb), 0.05);
      // border-color: rgba(var(--bs-success-rgb), 0.2);
   }
   &.option-incorrect {
       // background-color: rgba(var(--bs-danger-rgb), 0.05);
      // border-color: rgba(var(--bs-danger-rgb), 0.2);
   }
   &.option-missed-correct {
       // Slightly different background? Or just keep default?
       // border: 1px dashed rgba(var(--bs-success-rgb), 0.3); // Dashed border?
   }
}

.option-icon i {
  transition: color 0.3s ease;
}


.text-answer-content {
  // Styling for the user's text answer
}

.bg-success-light {
    background-color: rgba(var(--bs-success-rgb), 0.1);
}
.text-success {
    color: var(--bs-success) !important;
}
.bg-danger-light {
    background-color: rgba(var(--bs-danger-rgb), 0.1);
}
.text-danger {
     color: var(--bs-danger) !important;
}
.border-success-light {
    border-color: rgba(var(--bs-success-rgb), 0.3) !important;
}
.border-danger-light {
    border-color: rgba(var(--bs-danger-rgb), 0.3) !important;
}

// Remove default styles that are now handled by helpers
// .answer-correct { ... }
// .answer-incorrect { ... }
</style>
